import React from 'react'
import Page from 'components/Page'
import PageTitle from 'components/Page/Page.Title'
import PageSubTitle from 'components/Page/Page.SubTitle'
import TextTitleCompressedHeavy from 'components/TextTitleCompressedHeavy'
import TextBodySmaller from 'components/TextBodySmaller'
import TextBody from 'components/TextBody'
import { defineMessages } from 'react-intl'
import useIntl from 'modules/component/utils/hooks/useIntl'
import formatMessageIntlUtil from 'modules/intl/utils/format-message'
import useSelector from 'modules/component/utils/hooks/useSelector'
import useDispatch from 'modules/component/utils/hooks/useDispatch'
import {
  EXERCISE_KEY,
  getByIdExerciseSelector,
  getByIdMediaSelector,
  getVideoMediaIdByExerciseIdSelector,
  loadByIdExerciseAction,
  MEDIA_MODEL_KEY
  , SUBSCRIPTION_PLAN
} from '@gymaholic/packages'
import { View } from 'components/View'
import Block from 'components/Block'
import isSmallScreenViewportSelect from 'modules/viewport/selectors/is-small-screen'
import getLocationRouterSelect from 'modules/router/selectors/get-location'
import getValueReduxUtil from 'modules/redux/utils/get-value'
import { createExerciseFormCheckAction } from 'modules/exercise-form-check/redux/actions/create'
import getCurrentTrainingUserSelect from 'modules/training/modules/training-users/selectors/get-current'
import { hasPremiumUserSelect } from 'modules/training/modules/training-users/selectors/has-premium'
import openAppSignInModalAction from 'modules/modal/actions/open-app-sign-in'
import openSuccessBannerAction from 'modules/banner/actions/open-success'
import openErrorBannerAction from 'modules/banner/actions/open-error'
import { useTheme } from 'modules/styles'
import ScoreDetailModal from 'modules/exercise-form-check/components/ScoreDetailModal'
import logEventAnalyticsUtil from 'modules/analytics/utils/log-event'
import { ROUTE_PATH } from 'modules/routes/constants/index.cjs'
import { getUrlWithQueryStringUtil } from 'packages/src'
import pushRouterAction from 'modules/router/actions/push'
import { ReferenceVideoSection } from './components/ReferenceVideoSection'
import { UploadVideoSection } from './components/UploadVideoSection'
import { ResultsSection } from './components/ResultsSection'
import { UploadAnotherVideoSection } from './components/UploadAnotherVideoSection'
import { ToolsFAQ } from 'pages/Tools/components/ToolsFAQ'
import scrollToTopDomUtil from 'client/utils/dom/scroll-to-top'

const messages = defineMessages({
  title: {
    id: 'ToolsCheckExerciseFormPage.title',
    defaultMessage: 'AI Exercise Form Checker | Upload Video & Get Instant Feedback - Gymaholic'
  },
  metaDescription: {
    id: 'ToolsCheckExerciseFormPage.metaDescription',
    defaultMessage: 'Upload your exercise video and get instant AI feedback on your form, technique, and posture. Perfect your workout form with smart analysis in seconds.'
  },
  headline: {
    id: 'ToolsCheckExerciseFormPage.headline',
    defaultMessage: 'Check Your Exercise Form'
  },
  headlineWithExercise: {
    id: 'ToolsCheckExerciseFormPage.headlineWithExercise',
    defaultMessage: 'Check Your {exerciseName} Form'
  },
  description: {
    id: 'ToolsCheckExerciseFormPage.description',
    defaultMessage: 'Upload a video of your exercise and get instant AI feedback'
  },
  successBanner: {
    id: 'ToolsCheckExerciseFormPage.successBanner',
    defaultMessage: 'Form check completed successfully!'
  },
  errorBanner: {
    id: 'ToolsCheckExerciseFormPage.errorBanner',
    defaultMessage: 'Error submitting form check. Please try again.'
  },
  overallScore: {
    id: 'ToolsCheckExerciseFormPage.overallScore',
    defaultMessage: 'Overall Score'
  },
  control: {
    id: 'ToolsCheckExerciseFormPage.control',
    defaultMessage: 'Control'
  },
  rangeOfMotion: {
    id: 'ToolsCheckExerciseFormPage.rangeOfMotion',
    defaultMessage: 'Range of Motion'
  },
  tempo: {
    id: 'ToolsCheckExerciseFormPage.tempo',
    defaultMessage: 'Tempo'
  },
  alignment: {
    id: 'ToolsCheckExerciseFormPage.alignment',
    defaultMessage: 'Alignment'
  },
  symmetry: {
    id: 'ToolsCheckExerciseFormPage.symmetry',
    defaultMessage: 'Symmetry'
  },
  uploadingVideo: {
    id: 'ToolsCheckExerciseFormPage.uploadingVideo',
    defaultMessage: 'Uploading video...'
  },
  analyzingForm: {
    id: 'ToolsCheckExerciseFormPage.analyzingForm',
    defaultMessage: 'Analyzing form...'
  },
  processingResults: {
    id: 'ToolsCheckExerciseFormPage.processingResults',
    defaultMessage: 'Processing results...'
  },
  almostDone: {
    id: 'ToolsCheckExerciseFormPage.almostDone',
    defaultMessage: 'Hang tight! We\'re almost done...'
  },
  videoTooLong: {
    id: 'ToolsCheckExerciseFormPage.videoTooLong',
    defaultMessage: 'Video must be {maxDuration} seconds or less'
  },
  pleaseSelectVideo: {
    id: 'ToolsCheckExerciseFormPage.pleaseSelectVideo',
    defaultMessage: 'Please select a video'
  },
  howItWorksHeading: {
    id: 'ToolsCheckExerciseFormPage.howItWorksHeading',
    defaultMessage: 'How It Works'
  },
  howItWorksStep1Title: {
    id: 'ToolsCheckExerciseFormPage.howItWorksStep1Title',
    defaultMessage: 'Upload Your Video'
  },
  howItWorksStep1Description: {
    id: 'ToolsCheckExerciseFormPage.howItWorksStep1Description',
    defaultMessage: 'Capture your exercise clearly from a 45-degree angle.'
  },
  howItWorksStep2Title: {
    id: 'ToolsCheckExerciseFormPage.howItWorksStep2Title',
    defaultMessage: 'AI Analyzes Your Movement'
  },
  howItWorksStep2Description: {
    id: 'ToolsCheckExerciseFormPage.howItWorksStep2Description',
    defaultMessage: 'Evaluates coordination, tempo, and posture.'
  },
  howItWorksStep3Title: {
    id: 'ToolsCheckExerciseFormPage.howItWorksStep3Title',
    defaultMessage: 'Get Actionable Feedback'
  },
  howItWorksStep3Description: {
    id: 'ToolsCheckExerciseFormPage.howItWorksStep3Description',
    defaultMessage: 'See your performance score and improvements.'
  },
  howItWorksStep4Title: {
    id: 'ToolsCheckExerciseFormPage.howItWorksStep4Title',
    defaultMessage: 'Train Efficiently'
  },
  howItWorksStep4Description: {
    id: 'ToolsCheckExerciseFormPage.howItWorksStep4Description',
    defaultMessage: 'Refine your technique with every session.'
  }
})

const MAX_VIDEO_DURATION = 20 // seconds

const ANALYTIC_EVENT = {
  CHECK_EXERCISE_FORM_UPLOAD_VIDEO_CLICK: 'CHECK_EXERCISE_FORM_UPLOAD_VIDEO_CLICK',
  CHECK_EXERCISE_FORM_FILE_SELECTED: 'CHECK_EXERCISE_FORM_FILE_SELECTED',
  CHECK_EXERCISE_FORM_SUBMIT: 'CHECK_EXERCISE_FORM_SUBMIT',
  CHECK_EXERCISE_FORM_UPLOAD_SUCCESS: 'CHECK_EXERCISE_FORM_UPLOAD_SUCCESS',
  CHECK_EXERCISE_FORM_UPLOAD_ERROR: 'CHECK_EXERCISE_FORM_UPLOAD_ERROR',
  CHECK_EXERCISE_FORM_REMOVE_REFERENCE: 'CHECK_EXERCISE_FORM_REMOVE_REFERENCE',
  CHECK_EXERCISE_FORM_SCORE_CARD_CLICK: 'CHECK_EXERCISE_FORM_SCORE_CARD_CLICK',
  CHECK_EXERCISE_FORM_UPLOAD_ANOTHER_VIDEO: 'CHECK_EXERCISE_FORM_UPLOAD_ANOTHER_VIDEO',
  CHECK_EXERCISE_FORM_REDIRECT_TO_PREMIUM: 'CHECK_EXERCISE_FORM_REDIRECT_TO_PREMIUM'
}

export default function ToolsCheckExerciseFormPage (props) {
  const intl = useIntl()
  const dispatch = useDispatch()
  const isSmallScreen = useSelector(isSmallScreenViewportSelect)
  const location = useSelector(getLocationRouterSelect)
  const trainingUser = useSelector(getCurrentTrainingUserSelect)
  const hasPremium = useSelector(hasPremiumUserSelect)
  const theme = useTheme()

  const searchParams = new URLSearchParams(location.search)
  const exerciseIdFromQuery = searchParams.get('exerciseId')
  const sourceFromQuery = searchParams.get('source') || 'CHECK_EXERCISE_FORM_TOOL'

  const [exerciseId, setExerciseId] = React.useState(exerciseIdFromQuery ? Number(exerciseIdFromQuery) : null)
  const [selectedFile, setSelectedFile] = React.useState(null)
  const [previewVideoUrl, setPreviewVideoUrl] = React.useState(null)
  const [isUploadingVideo, setIsUploadingVideo] = React.useState(false)
  const [isAnalyzingForm, setIsAnalyzingForm] = React.useState(false)
  const [loadingStep, setLoadingStep] = React.useState(0) // 0: not loading, 1: uploading, 2: analyzing, 3: processing, 4: almost done
  const [formCheckResult, setFormCheckResult] = React.useState(null)
  const [selectedScoreDetail, setSelectedScoreDetail] = React.useState(null)
  const videoInputRef = React.useRef(null)

  const exercise = useSelector(getByIdExerciseSelector(exerciseId))
  const mediaVideoId = useSelector(getVideoMediaIdByExerciseIdSelector(exerciseId))
  const media = useSelector(getByIdMediaSelector(mediaVideoId))
  const posterUrl = getValueReduxUtil(media, MEDIA_MODEL_KEY.THUMBNAIL)
  const videoUrl = getValueReduxUtil(media, MEDIA_MODEL_KEY.URL)
  const exerciseName = getValueReduxUtil(exercise, EXERCISE_KEY.NAME)

  React.useEffect(() => {
    // Scroll to top when component mounts
    scrollToTopDomUtil()
  }, [])

  React.useEffect(() => {
    if (exerciseId && !exercise) {
      dispatch(loadByIdExerciseAction(exerciseId))
    }
  }, [
    exerciseId,
    exercise,
    dispatch
  ])

  const handleUploadClick = (event) => {
    // Prevent any default behavior
    event.preventDefault()
    event.stopPropagation()

    // Analytics
    if (formCheckResult) {
      logEventAnalyticsUtil(ANALYTIC_EVENT.CHECK_EXERCISE_FORM_UPLOAD_ANOTHER_VIDEO)
    } else {
      logEventAnalyticsUtil(ANALYTIC_EVENT.CHECK_EXERCISE_FORM_UPLOAD_VIDEO_CLICK)
    }

    // Check if user is logged in
    if (!trainingUser) {
      dispatch(openAppSignInModalAction())
      return
    }

    // Check if user has premium
    if (!hasPremium) {
      // Analytics
      logEventAnalyticsUtil(ANALYTIC_EVENT.CHECK_EXERCISE_FORM_REDIRECT_TO_PREMIUM)

      // Redirect to premium page
      const premiumUrl = getUrlWithQueryStringUtil(ROUTE_PATH.PREMIUM, { plan: SUBSCRIPTION_PLAN.PREMIUM_YEARLY_2025 })
      dispatch(pushRouterAction(premiumUrl))
      return
    }

    // Clear results if they exist
    if (formCheckResult) {
      setFormCheckResult(null)
    }

    // Open file picker
    videoInputRef.current?.click()
  }

  const handleFileChange = (event) => {
    const file = event.target.files[0]
    if (file) {
      // Analytics
      logEventAnalyticsUtil(ANALYTIC_EVENT.CHECK_EXERCISE_FORM_FILE_SELECTED, {
        fileSize: file.size,
        fileType: file.type
      })

      // Check video duration
      const video = document.createElement('video')
      video.preload = 'metadata'
      video.onloadedmetadata = function () {
        const videoUrl = URL.createObjectURL(file)
        if (video.duration > MAX_VIDEO_DURATION) {
          dispatch(openErrorBannerAction(
            formatMessageIntlUtil(intl, messages.videoTooLong, { maxDuration: MAX_VIDEO_DURATION })
          ))
          window.URL.revokeObjectURL(videoUrl)
          return
        }
        setSelectedFile(file)
        setPreviewVideoUrl(videoUrl)
      }
      video.src = URL.createObjectURL(file)
    }
  }

  const handleRemoveReference = () => {
    // Analytics
    logEventAnalyticsUtil(ANALYTIC_EVENT.CHECK_EXERCISE_FORM_REMOVE_REFERENCE, {
      exerciseId
    })

    setExerciseId(null)
    // Update URL to remove exerciseId
    const newSearchParams = new URLSearchParams(location.search)
    newSearchParams.delete('exerciseId')
    window.history.replaceState({}, '', `${location.pathname}?${newSearchParams.toString()}`)
  }

  const handleSubmit = async () => {
    // Check if user is logged in
    if (!trainingUser) {
      dispatch(openAppSignInModalAction())
      return
    }

    if (!selectedFile) {
      dispatch(openErrorBannerAction(formatMessageIntlUtil(intl, messages.pleaseSelectVideo)))
      return
    }

    // Analytics
    logEventAnalyticsUtil(ANALYTIC_EVENT.CHECK_EXERCISE_FORM_SUBMIT, {
      exerciseId,
      source: sourceFromQuery,
      hasReferenceExercise: !!exerciseId
    })

    try {
      // Step 1: Uploading video
      setLoadingStep(1)
      setIsUploadingVideo(true)

      // Start the API call
      const resultPromise = dispatch(createExerciseFormCheckAction({
        file: selectedFile,
        exerciseId,
        source: sourceFromQuery
      }))

      // Wait 5 seconds before moving to step 2
      await new Promise(resolve => setTimeout(resolve, 5000))

      // Step 2: Analyzing form
      setLoadingStep(2)
      setIsUploadingVideo(false)
      setIsAnalyzingForm(true)

      // Wait 5 seconds before moving to step 3
      await new Promise(resolve => setTimeout(resolve, 5000))

      // Step 3: Processing results
      setLoadingStep(3)

      // Wait 5 seconds before moving to step 4
      await new Promise(resolve => setTimeout(resolve, 5000))

      // Step 4: Almost done
      setLoadingStep(4)

      // Wait for the actual result
      const result = await resultPromise

      // Store the result
      setFormCheckResult(result?.data?.exerciseFormCheck || null)

      // Analytics - Success
      logEventAnalyticsUtil(ANALYTIC_EVENT.CHECK_EXERCISE_FORM_UPLOAD_SUCCESS, {
        exerciseId,
        overallScore: result?.data?.exerciseFormCheck?.overallScore,
        exerciseName: result?.data?.exerciseFormCheck?.exerciseName
      })

      // Show success banner
      dispatch(openSuccessBannerAction(formatMessageIntlUtil(intl, messages.successBanner)))

      // Clear selected file and preview
      setSelectedFile(null)
      setPreviewVideoUrl(null)
    } catch (error) {
      console.error('Error submitting form check:', error)

      // Analytics - Error
      logEventAnalyticsUtil(ANALYTIC_EVENT.CHECK_EXERCISE_FORM_UPLOAD_ERROR, {
        error: error.message
      })

      dispatch(openErrorBannerAction(formatMessageIntlUtil(intl, messages.errorBanner)))
    } finally {
      setIsUploadingVideo(false)
      setIsAnalyzingForm(false)
      setLoadingStep(0)
    }
  }

  const handleScoreCardClick = (scoreType) => {
    if (!formCheckResult) return

    // Analytics
    logEventAnalyticsUtil(ANALYTIC_EVENT.CHECK_EXERCISE_FORM_SCORE_CARD_CLICK, {
      scoreType,
      exerciseName: formCheckResult.exerciseName
    })

    const scoreData = {
      overallScore: {
        emoji: '🎯',
        title: formatMessageIntlUtil(intl, messages.overallScore),
        score: formCheckResult.overallScore,
        summary: formCheckResult.overallScoreSummary
      },
      control: {
        emoji: '⚖️',
        title: formatMessageIntlUtil(intl, messages.control),
        score: formCheckResult.controlScore,
        summary: formCheckResult.controlScoreSummary
      },
      range: {
        emoji: '📏',
        title: formatMessageIntlUtil(intl, messages.rangeOfMotion),
        score: formCheckResult.rangeScore,
        summary: formCheckResult.rangeScoreSummary
      },
      tempo: {
        emoji: '⏱️',
        title: formatMessageIntlUtil(intl, messages.tempo),
        score: formCheckResult.tempoScore,
        summary: formCheckResult.tempoScoreSummary
      },
      alignment: {
        emoji: '📐',
        title: formatMessageIntlUtil(intl, messages.alignment),
        score: formCheckResult.alignmentScore,
        summary: formCheckResult.alignmentScoreSummary
      },
      symmetry: {
        emoji: '↔️',
        title: formatMessageIntlUtil(intl, messages.symmetry),
        score: formCheckResult.symmetryScore,
        summary: formCheckResult.symmetryScoreSummary
      }
    }

    setSelectedScoreDetail(scoreData[scoreType])
  }

  const handleCloseModal = () => {
    setSelectedScoreDetail(null)
  }

  const headline = exerciseName
    ? formatMessageIntlUtil(intl, messages.headlineWithExercise, { exerciseName })
    : formatMessageIntlUtil(intl, messages.headline)

  // Get loading label based on step
  const getLoadingLabel = () => {
    if (loadingStep === 1) {
      return formatMessageIntlUtil(intl, messages.uploadingVideo)
    }
    if (loadingStep === 2) {
      return formatMessageIntlUtil(intl, messages.analyzingForm)
    }
    if (loadingStep === 3) {
      return formatMessageIntlUtil(intl, messages.processingResults)
    }
    if (loadingStep === 4) {
      return formatMessageIntlUtil(intl, messages.almostDone)
    }
    return null
  }

  return (
    <Page
      shouldNotHavePadding
      title={formatMessageIntlUtil(intl, messages.title)}
      metaDescription={formatMessageIntlUtil(intl, messages.metaDescription)}
    >
      <Block
        style={{
          minHeight: 'auto',
          flexGrow: '1',
          padding: isSmallScreen
            ? '1.5rem 2rem 3rem'
            : '4rem 8rem 4rem'
        }}
      >
        <View centered>
          {/* Page Title */}
          <View>
            <PageTitle>
              {headline}
            </PageTitle>
          </View>

          {/* Page Description */}
          <View mt>
            <PageSubTitle style={{ textAlign: 'center' }}>
              {formatMessageIntlUtil(intl, messages.description)}
            </PageSubTitle>
          </View>

          <View
            mt={2}
            style={{
              width: '100%',
              maxWidth: theme.maxWidth.desktopCompact
            }}
          >

            {/* Reference Video Section */}
            <ReferenceVideoSection
              videoUrl={videoUrl}
              posterUrl={posterUrl}
              onRemove={handleRemoveReference}
            />

            {/* Video Upload Section - Show first when no results */}
            {!formCheckResult && (
              <UploadVideoSection
                selectedFile={selectedFile}
                previewVideoUrl={previewVideoUrl}
                videoInputRef={videoInputRef}
                isUploadingVideo={isUploadingVideo}
                isAnalyzingForm={isAnalyzingForm}
                loadingLabel={getLoadingLabel()}
                onUploadClick={handleUploadClick}
                onFileChange={handleFileChange}
                onSubmit={handleSubmit}
              />
            )}

            {/* Results Section */}
            {formCheckResult && (
              <ResultsSection
                formCheckResult={formCheckResult}
                onScoreCardClick={handleScoreCardClick}
              />
            )}

            {/* Video Upload Section - Show after results */}
            {formCheckResult && (
              <UploadAnotherVideoSection
                videoInputRef={videoInputRef}
                isUploadingVideo={isUploadingVideo}
                isAnalyzingForm={isAnalyzingForm}
                onUploadClick={handleUploadClick}
                onFileChange={handleFileChange}
              />
            )}

            {/* Score Detail Modal */}
            {selectedScoreDetail && (
              <ScoreDetailModal
                title={selectedScoreDetail.title}
                score={selectedScoreDetail.score}
                summary={selectedScoreDetail.summary}
                emoji={selectedScoreDetail.emoji}
                onClose={handleCloseModal}
              />
            )}
          </View>
        </View>
      </Block>

      {/* How It Works Section */}
      <Block
        bgc='backgroundContent'
      >
        <View
          centered
          style={{
            maxWidth: theme.maxWidth.desktopCompact
          }}
        >
          <View mb={3}>
            <TextTitleCompressedHeavy>
              {formatMessageIntlUtil(intl, messages.howItWorksHeading)}
            </TextTitleCompressedHeavy>
          </View>

          <View style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
            {/* Step 1 */}
            <View mb={3} style={{ display: 'flex', flexDirection: 'row', alignItems: 'flex-start', gap: '1rem' }}>
              <View style={{
                width: '48px',
                height: '48px',
                borderRadius: '50%',
                backgroundColor: 'black',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}
              >
                <TextBody style={{ color: 'white', fontWeight: 'bold', fontSize: '1.25rem' }}>1</TextBody>
              </View>
              <View style={{ flex: 1, textAlign: 'left' }}>
                <View mb={0.5}>
                  <TextBody style={{ fontWeight: 'bold' }}>
                    {formatMessageIntlUtil(intl, messages.howItWorksStep1Title)}
                  </TextBody>
                </View>
                <TextBodySmaller style={{ color: theme.colors.paragraph }}>
                  {formatMessageIntlUtil(intl, messages.howItWorksStep1Description)}
                </TextBodySmaller>
              </View>
            </View>

            {/* Step 2 */}
            <View mb={3} style={{ display: 'flex', flexDirection: 'row', alignItems: 'flex-start', gap: '1rem' }}>
              <View style={{
                width: '48px',
                height: '48px',
                borderRadius: '50%',
                backgroundColor: 'black',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}
              >
                <TextBody style={{ color: 'white', fontWeight: 'bold', fontSize: '1.25rem' }}>2</TextBody>
              </View>
              <View style={{ flex: 1, textAlign: 'left' }}>
                <View mb={0.5}>
                  <TextBody style={{ fontWeight: 'bold' }}>
                    {formatMessageIntlUtil(intl, messages.howItWorksStep2Title)}
                  </TextBody>
                </View>
                <TextBodySmaller style={{ color: theme.colors.paragraph }}>
                  {formatMessageIntlUtil(intl, messages.howItWorksStep2Description)}
                </TextBodySmaller>
              </View>
            </View>

            {/* Step 3 */}
            <View mb={3} style={{ display: 'flex', flexDirection: 'row', alignItems: 'flex-start', gap: '1rem' }}>
              <View style={{
                width: '48px',
                height: '48px',
                borderRadius: '50%',
                backgroundColor: 'black',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}
              >
                <TextBody style={{ color: 'white', fontWeight: 'bold', fontSize: '1.25rem' }}>3</TextBody>
              </View>
              <View style={{ flex: 1, textAlign: 'left' }}>
                <View mb={0.5}>
                  <TextBody style={{ fontWeight: 'bold' }}>
                    {formatMessageIntlUtil(intl, messages.howItWorksStep3Title)}
                  </TextBody>
                </View>
                <TextBodySmaller style={{ color: theme.colors.paragraph }}>
                  {formatMessageIntlUtil(intl, messages.howItWorksStep3Description)}
                </TextBodySmaller>
              </View>
            </View>

            {/* Step 4 */}
            <View style={{ display: 'flex', flexDirection: 'row', alignItems: 'flex-start', gap: '1rem' }}>
              <View style={{
                width: '48px',
                height: '48px',
                borderRadius: '50%',
                backgroundColor: 'black',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}
              >
                <TextBody style={{ color: 'white', fontWeight: 'bold', fontSize: '1.25rem' }}>4</TextBody>
              </View>
              <View style={{ flex: 1, textAlign: 'left' }}>
                <View mb={0.5}>
                  <TextBody style={{ fontWeight: 'bold' }}>
                    {formatMessageIntlUtil(intl, messages.howItWorksStep4Title)}
                  </TextBody>
                </View>
                <TextBodySmaller style={{ color: theme.colors.paragraph }}>
                  {formatMessageIntlUtil(intl, messages.howItWorksStep4Description)}
                </TextBodySmaller>
              </View>
            </View>
          </View>
        </View>
      </Block>

      {/* FAQ Section */}
      <ToolsFAQ />
    </Page>
  )
}
